import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Hand, Eye, Wifi, Shield, Zap, Building2, Package, Brain, Lock, Sparkles } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { PreOrderForm } from "@/components/PreOrderForm";
import flashbotArmImg from "@/assets/robots/flashbot-arm-main.jpeg";
import actionVideo from "@/assets/robots/flashbot-arm-action.mov";

const FlashBotArm = () => {
  const [preOrderOpen, setPreOrderOpen] = useState(false);

  const specifications = [
    {
      icon: <Hand className="w-5 h-5" />,
      label: "Robotic Arms",
      value: "Dual 7-DOF Arms"
    },
    {
      icon: <Eye className="w-5 h-5" />,
      label: "Perception",
      value: "Multi-Modal Vision System"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      label: "Manipulation",
      value: "Dexterous Hands"
    },
    {
      icon: <Brain className="w-5 h-5" />,
      label: "AI Intelligence",
      value: "Embodied AI System"
    }
  ];

  const keyFeatures = [
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Seamless Navigation",
      description: "Effortlessly navigates complex environments - presses elevator buttons, swipes access cards, and opens doors autonomously without human intervention."
    },
    {
      icon: <Hand className="w-6 h-6" />,
      title: "Independent Operation",
      description: "Dual 7-DOF robotic arms with dexterous hands enable precise manipulation tasks and a wide array of responsibilities from delivery to complex interactions."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Multi-Modal AI Interaction",
      description: "Advanced AI-powered communication system enables natural human-robot interaction through voice, gesture recognition, and visual feedback."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Comprehensive Safety",
      description: "Equipped with comprehensive vision and tactile sensors combined with compliant force control algorithms for safe operation in human environments."
    },
    {
      icon: <Wifi className="w-6 h-6" />,
      title: "IoT Integration",
      description: "Full integration with building systems including elevators, automatic doors, access control systems, and smart building infrastructure."
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Secure Delivery",
      description: "Multiple authentication methods including facial recognition, QR codes, and PIN codes ensure secure delivery and recipient verification."
    }
  ];

  const applications = [
    {
      title: "Office Buildings",
      description: "Autonomous mail delivery, document distribution, supply transport between floors and departments with automatic elevator operation."
    },
    {
      title: "Hotels",
      description: "Guest room delivery of amenities, towels, and room service items. Autonomous navigation to any floor with door interaction capability."
    },
    {
      title: "Hospitals",
      description: "Medication delivery, lab sample transport, supply distribution across departments with sterile handling protocols and secure access."
    },
    {
      title: "Research Facilities",
      description: "Equipment delivery, sample transport, and facility support with capability to operate elevators and navigate restricted access areas."
    },
    {
      title: "Corporate Campuses",
      description: "Building-to-building delivery, cafeteria service, mailroom operations with outdoor navigation capabilities and weather resistance."
    },
    {
      title: "Manufacturing",
      description: "Parts delivery, tool transport, quality inspection support with ability to manipulate objects and interact with industrial equipment."
    }
  ];

  const technicalCapabilities = [
    {
      category: "Manipulation",
      specs: [
        "Dual 7-DOF robotic arms for complex tasks",
        "Dexterous hands with precise grip control",
        "Force-feedback sensors for delicate handling",
        "Wide range of motion for versatile operations"
      ]
    },
    {
      category: "Perception & AI",
      specs: [
        "Multi-camera vision system with depth sensing",
        "Real-time object detection and recognition",
        "Embodied AI for autonomous decision-making",
        "Natural language processing for voice commands"
      ]
    },
    {
      category: "Navigation",
      specs: [
        "VSLAM+ autonomous navigation system",
        "Elevator button recognition and pressing",
        "Automatic door opening capability",
        "Access card swiping for secure areas"
      ]
    },
    {
      category: "Safety & Compliance",
      specs: [
        "Comprehensive tactile sensor array",
        "Compliant force control algorithms",
        "Emergency stop functionality",
        "Collision avoidance system"
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="FlashBot Arm - Semi-Humanoid Service Robot | Pre-Order Now"
        description="Next-generation embodied AI robot with dual 7-DOF robotic arms for autonomous delivery and manipulation. Operates elevators, doors, and performs complex tasks independently."
        keywords="FlashBot Arm, semi-humanoid robot, robotic arms, delivery robot, building automation, embodied AI, service robot"
      />
      <Navigation />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 pt-12 pb-8 md:pt-16">
          {/* Back Button */}
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Robots
            </Link>
          </Button>

          {/* Hero Section */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="relative bg-white/5 rounded-lg p-8 flex items-center justify-center">
              <img 
                src={flashbotArmImg} 
                alt="FlashBot Arm - Semi-Humanoid Embodied AI Service Robot" 
                className="w-full h-auto max-h-[600px] object-contain"
              />
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground font-bold text-lg px-4 py-2">
                PRE-ORDER OPEN
              </Badge>
            </div>

            <div className="flex flex-col justify-center space-y-6">
              <div>
                <p className="text-primary font-semibold mb-2">Semi-Humanoid Embodied AI Service Robot</p>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">FlashBot Arm</h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground mb-6 leading-relaxed">
                  Next-generation embodied AI robot with dual 7-DOF robotic arms for autonomous delivery and manipulation. Effortlessly operates elevators, doors, and performs complex tasks.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <p className="text-xs font-semibold">{spec.label}</p>
                      </div>
                      <p className="text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex gap-4">
                <Button 
                  size="lg" 
                  className="flex-1 text-lg glow-gold"
                  onClick={() => setPreOrderOpen(true)}
                >
                  Pre-Order Now
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  variant="outline" 
                  className="flex-1 text-lg"
                >
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book a Demo
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="flex w-full mb-8 overflow-x-auto scrollbar-hide">
              <TabsTrigger value="overview" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Overview</TabsTrigger>
              <TabsTrigger value="specifications" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Specs</TabsTrigger>
              <TabsTrigger value="applications" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Applications</TabsTrigger>
              <TabsTrigger value="action" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">See In Action</TabsTrigger>
              <TabsTrigger value="accessories" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Add-Ons</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Revolutionary Semi-Humanoid Design</CardTitle>
                  <CardDescription>
                    FlashBot Arm combines advanced robotics with embodied AI for unprecedented autonomous operation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    FlashBot Arm represents a paradigm shift in service robotics, featuring dual 7-DOF (degrees of freedom) robotic arms that enable complex manipulation tasks previously impossible for autonomous robots. Unlike traditional delivery robots, FlashBot Arm can independently interact with its environment - pressing elevator buttons, swiping access cards, opening doors, and manipulating objects with precision.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    The embodied AI system enables natural human-robot interaction through multi-modal communication including voice recognition, gesture detection, and visual feedback. This allows for intuitive operation and seamless integration into existing workflows without extensive training.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Safety is paramount with comprehensive vision and tactile sensors combined with compliant force control algorithms. The robot can detect obstacles, adjust its grip force, and respond appropriately to unexpected situations, making it safe to operate in busy human environments.
                  </p>
                </CardContent>
              </Card>

              <div>
                <h2 className="font-orbitron text-2xl font-bold mb-6">Key Features</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {keyFeatures.map((feature, idx) => (
                    <Card key={idx} className="glass-effect border-[hsl(var(--line))] hover:scale-105 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="text-primary mb-3">{feature.icon}</div>
                        <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8">
                  <h3 className="font-orbitron text-xl font-bold mb-4">Why Choose FlashBot Arm?</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold mb-2 text-primary">Advanced Capabilities</h4>
                      <ul className="space-y-2 text-sm">
                        <li>✅ Autonomous elevator operation</li>
                        <li>✅ Door opening and access control</li>
                        <li>✅ Complex object manipulation</li>
                        <li>✅ Multi-floor navigation</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold mb-2 text-primary">Business Benefits</h4>
                      <ul className="space-y-2 text-sm">
                        <li>✅ Reduce labor costs by 60%</li>
                        <li>✅ 24/7 operation capability</li>
                        <li>✅ Improve delivery efficiency</li>
                        <li>✅ Enhance customer experience</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specifications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Technical Specifications</CardTitle>
                  <CardDescription>Detailed technical capabilities of the FlashBot Arm</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {technicalCapabilities.map((section, idx) => (
                      <div key={idx}>
                        <h3 className="font-bold text-lg mb-4 text-primary">{section.category}</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          {section.specs.map((spec, specIdx) => (
                            <div key={specIdx} className="flex items-start gap-2">
                              <Package className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                              <p className="text-sm">{spec}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Connectivity & Integration</h3>
                      <div className="space-y-2">
                        <p className="text-sm">• <span className="font-semibold">IoT Integration:</span> Full building system integration</p>
                        <p className="text-sm">• <span className="font-semibold">Cloud Connectivity:</span> Remote monitoring and updates</p>
                        <p className="text-sm">• <span className="font-semibold">API Access:</span> Custom integration capabilities</p>
                        <p className="text-sm">• <span className="font-semibold">Fleet Management:</span> Multi-robot coordination</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Physical Specifications</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                          <p className="text-xs text-muted-foreground mb-1">Operating Time</p>
                          <p className="font-bold">8-12 hours</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                          <p className="text-xs text-muted-foreground mb-1">Charging Time</p>
                          <p className="font-bold">3-4 hours</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                          <p className="text-xs text-muted-foreground mb-1">Payload Capacity</p>
                          <p className="font-bold">Up to 30kg</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Industry Applications</CardTitle>
                  <CardDescription>
                    FlashBot Arm revolutionizes service delivery across multiple industries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="space-y-2 p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                        <h3 className="font-bold text-lg text-primary">{app.title}</h3>
                        <p className="text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8 text-center">
                  <h3 className="font-orbitron text-2xl font-bold mb-4">Ready to Transform Your Operations?</h3>
                  <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                    Be among the first to deploy next-generation semi-humanoid robotics. Pre-order your FlashBot Arm today and revolutionize your service delivery.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg" 
                      className="glow-gold"
                      onClick={() => setPreOrderOpen(true)}
                    >
                      Pre-Order Now
                    </Button>
                    <Button asChild size="lg" variant="outline">
                      <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                        Schedule Consultation
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="action" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">FlashBot Arm In Action</CardTitle>
                  <CardDescription>
                    Watch the FlashBot Arm demonstrate its advanced manipulation and autonomous capabilities
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-black/50">
                    <video 
                      controls 
                      className="w-full h-full object-contain"
                      poster={flashbotArmImg}
                      preload="metadata"
                      playsInline
                    >
                      <source src={actionVideo} type="video/quicktime" />
                      <source src={actionVideo} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <div className="mt-6">
                    <p className="text-muted-foreground mb-6">
                      Experience the revolutionary capabilities of FlashBot Arm as it demonstrates autonomous elevator operation, door interaction, object manipulation with its dual 7-DOF robotic arms, and seamless navigation through complex building environments.
                    </p>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div className="p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                        <h4 className="font-bold mb-2 text-primary">Featured Capabilities</h4>
                        <ul className="space-y-2 text-sm">
                          <li>✅ Autonomous elevator button pressing</li>
                          <li>✅ Door opening with robotic arms</li>
                          <li>✅ Precise object manipulation</li>
                          <li>✅ Multi-floor navigation</li>
                        </ul>
                      </div>
                      <div className="p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                        <h4 className="font-bold mb-2 text-primary">AI-Powered Intelligence</h4>
                        <ul className="space-y-2 text-sm">
                          <li>✅ Real-time environment perception</li>
                          <li>✅ Autonomous decision making</li>
                          <li>✅ Natural interaction with humans</li>
                          <li>✅ Adaptive task execution</li>
                        </ul>
                      </div>
                    </div>
                    <div className="text-center">
                      <Button 
                        size="lg"
                        className="glow-gold mr-4"
                        onClick={() => setPreOrderOpen(true)}
                      >
                        Pre-Order Now
                      </Button>
                      <Button 
                        asChild 
                        size="lg" 
                        variant="outline"
                      >
                        <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                          Schedule a Demo
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="accessories" className="space-y-8">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">FlashBot Vending Machine Module</CardTitle>
                  <CardDescription>
                    Transform your FlashBot into a mobile retail and vending solution
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h3 className="font-bold text-lg mb-3 text-primary">Key Features</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Package className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm">Modular vending compartments with secure access control</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Lock className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm">Multiple payment options: QR code, NFC, and mobile payment integration</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Wifi className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm">Cloud-based inventory management and real-time stock monitoring</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm">Temperature control options for food, beverages, and sensitive items</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Zap className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm">Autonomous restocking alerts and route optimization</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-3 text-primary">Applications</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Building2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm"><strong>Office Buildings:</strong> Snacks, beverages, office supplies on-demand delivery to desks</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Building2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm"><strong>Hotels:</strong> Mini-bar replenishment, grab-and-go items, amenity sales to guest rooms</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Building2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm"><strong>Airports:</strong> Travel essentials, snacks, souvenirs at gates and lounges</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Building2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm"><strong>Hospitals:</strong> Pharmacy items, patient care products, cafeteria items</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Building2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <span className="text-sm"><strong>Universities:</strong> Campus-wide vending service, textbooks, study supplies</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                    <h4 className="font-bold mb-2 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      Revenue Enhancement
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      The vending module transforms FlashBot into a mobile revenue generator. Increase sales by bringing products directly to customers at their location, reduce labor costs with autonomous operation, and capture impulse purchases with strategic positioning throughout your facility.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-effect border-primary/30 bg-primary/5">
                <CardContent className="p-6 text-center">
                  <h3 className="font-orbitron text-xl font-bold mb-3">Interested in the Vending Module?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Contact us to learn more about adding the vending module to your FlashBot Arm configuration.
                  </p>
                  <Button asChild size="lg">
                    <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                      Schedule Consultation
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
      <PreOrderForm 
        open={preOrderOpen} 
        onOpenChange={setPreOrderOpen}
        robotName="FlashBot Arm"
      />
    </>
  );
};

export default FlashBotArm;
